<template>
    <div class="p-5 rounded-lgr border border-border-strong bg-surface-raised shadow-elevated flex flex-col gap-5">
        <PlaceholderFilters v-if="!isFetchingSSOFilters && ssoFilterConfig && ssoFilterConfig.filters.length === 0" />
        <AnalyticsSSOFilters v-else ref="ssoFiltersRef" :config="ssoFilterConfig" :is-fetching="isFetchingSSOFilters" @updated="selectedSSOFilters = $event" />
        <AnalyticsDateFilters v-if="showDateFilters" ref="dateFiltersRef" @updated="selectedDateFilters = $event" />
        <AnalyticsKAnonWarning v-if="showKAnonymizationError" @reset-filters="reset" />
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { ref, watch } from "vue";

import type { SelectedFilters, SSOFilterConfigResponse } from "../types";
import AnalyticsDateFilters, { DEFAULT_DATE_FILTERS } from "./AnalyticsDateFilters.vue";
import AnalyticsKAnonWarning from "./AnalyticsKAnonWarning.vue";
import AnalyticsSSOFilters from "./AnalyticsSSOFilters.vue";
import PlaceholderFilters from "./PlaceholderFilters.vue";

const { showDateFilters = false, showKAnonymizationError = false } = defineProps<{
    showDateFilters?: boolean;
    showKAnonymizationError?: boolean;
}>();

const dateFiltersRef = ref<InstanceType<typeof AnalyticsDateFilters> | null>(null);
const selectedDateFilters = ref({ ...DEFAULT_DATE_FILTERS });

const ssoFiltersRef = ref<InstanceType<typeof AnalyticsSSOFilters> | null>(null);
const selectedSSOFilters = ref<SelectedFilters>({});
const { data: ssoFilterConfig, isFetching: isFetchingSSOFilters } = useFetch("/api/client-analytics/filters/").json<SSOFilterConfigResponse>();

const emit = defineEmits<{
    updated: [filters: SelectedFilters];
    isFetchingUpdated: [isFetching: boolean];
}>();

watch(isFetchingSSOFilters, (newIsFetchingSSOFilters) => {
    emit("isFetchingUpdated", newIsFetchingSSOFilters);
});

watch(
    [selectedDateFilters, selectedSSOFilters],
    ([newDateFilters, newSSOFilters]) => {
        const newFilters = showDateFilters ? { ...newDateFilters, ...newSSOFilters } : newSSOFilters;
        emit("updated", newFilters);
    },
    { deep: true },
);

const reset = () => {
    dateFiltersRef.value?.reset();
    ssoFiltersRef.value?.reset();
    logUserInteraction(AmplitudeEventNames.CLIENT_ANALYTICS_FILTERS_CLEARED);
};

defineExpose({
    reset,
});

watch(
    () => showKAnonymizationError,
    (newShow, oldShow) => {
        if (newShow && !oldShow) {
            logUserInteraction(AmplitudeEventNames.CLIENT_ANALYTICS_K_ANONYMIZATION_WARNING_SHOWN);
        }
    },
    { immediate: true },
);
</script>
